import { useMemo, useRef, useState, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { CategoryHero } from './CategoryHero'
import { Prefooter, Footer } from '@/components/layout'
import { ProductCard } from '@/components/ui'
import {
  getProductsByCategory,
  lineLabels,
  type Category,
  type Line,
  type Product,
} from '@/data/products'

type FilterValue = 'all' | Line

export interface CategoryArchiveProps {
  category: Category
  hero: {
    eyebrow?: string
    title: string
    titleItalic?: string
    intro: string
    image: string
    imageAlt?: string
    breadcrumbs?: { label: string; to?: string }[]
  }
  /** Order of lines to display when 'all' filter is active. Lines not in the list won't show. */
  lineOrder: Line[]
  /** Optional editorial Q&A block at the bottom of the archive. */
  faq?: {
    eyebrow?: string
    title: string
    titleItalic?: string
    body: ReactNode
    closingNumber?: string
    closingNote?: ReactNode
  }
  labels?: {
    filterByLine?: string
    scrollLeft?: string
    scrollRight?: string
    all?: string
    linePrefix?: string
    productsSuffix?: string
    defaultEyebrow?: string
    defaultFaqEyebrow?: string
    categoryAriaLabel?: string
  }
}

export function CategoryArchive({
  category,
  hero,
  lineOrder,
  faq,
  labels,
}: CategoryArchiveProps) {
  const allProducts = useMemo(() => getProductsByCategory(category), [category])
  const [filter, setFilter] = useState<FilterValue>('all')

  /** Lines that actually have at least one product (avoid showing empty filters). */
  const availableLines = useMemo(() => {
    const present = new Set(allProducts.map((p) => p.line))
    return lineOrder.filter((line) => present.has(line))
  }, [allProducts, lineOrder])

  /** When "all" is selected, group products by line for the editorial layout. */
  const groupedAll = useMemo(() => {
    const groups: { line: Line; items: Product[] }[] = []
    for (const line of availableLines) {
      const items = allProducts.filter((p) => p.line === line)
      if (items.length) groups.push({ line, items })
    }
    return groups
  }, [allProducts, availableLines])

  const filteredFlat =
    filter === 'all' ? [] : allProducts.filter((p) => p.line === filter)

  const showFilterBar = availableLines.length > 1
  const scrollRef = useRef<HTMLDivElement>(null)

  const {
    filterByLine = 'Filtra per linea',
    scrollLeft = 'Scorri a sinistra',
    scrollRight = 'Scorri a destra',
    all = 'Tutti',
    linePrefix = 'Linea',
    productsSuffix = 'prodotti',
    defaultEyebrow = 'Categoria',
    defaultFaqEyebrow = 'FAQ collezione',
    categoryAriaLabel,
  } = labels ?? {}

  const scrollFilters = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -200 : 200,
      behavior: 'smooth',
    })
  }

  return (
    <main>
      <CategoryHero
        eyebrow={hero.eyebrow ?? defaultEyebrow}
        title={hero.title}
        titleItalic={hero.titleItalic}
        intro={hero.intro}
        image={hero.image}
        imageAlt={hero.imageAlt}
        productCount={allProducts.length}
        breadcrumbs={hero.breadcrumbs}
        ariaLabel={categoryAriaLabel}
      />

      {/* Filter bar — only when there are 2+ lines */}
      {showFilterBar && (
        <section
          aria-label={filterByLine}
          className="sticky top-16 z-30 bg-cream/85 backdrop-blur-md border-y border-bruno/10"
        >
          <div className="nashi-container flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollFilters('left')}
              className="shrink-0 p-1 text-bruno hover:text-argan-deep transition-colors"
              aria-label={scrollLeft}
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
            </button>

            <div
              ref={scrollRef}
              className="flex-1 overflow-x-auto scrollbar-hide"
            >
              <div className="flex items-center gap-2 md:gap-3 py-4">
                  <FilterChip
                    label={all}
                    count={allProducts.length}
                    active={filter === 'all'}
                    onClick={() => setFilter('all')}
                  />
                  {availableLines.map((line) => {
                    const count = allProducts.filter((p) => p.line === line).length
                    return (
                      <FilterChip
                        key={line}
                        label={lineLabels[line]}
                        count={count}
                        active={filter === line}
                        onClick={() => setFilter(line)}
                      />
                    )
                  })}
                </div>
              </div>

            <button
              type="button"
              onClick={() => scrollFilters('right')}
              className="shrink-0 p-1 text-bruno hover:text-argan-deep transition-colors"
              aria-label={scrollRight}
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="relative bg-cream py-20 md:py-28 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 right-[-6%] select-none font-display italic text-argan/[0.05]"
          style={{ fontSize: 'clamp(14rem, 30vw, 32rem)', lineHeight: 0.85 }}
        >
          n.
        </div>

        <div className="relative nashi-container">
          <AnimatePresence mode="wait">
            {filter === 'all' ? (
              <motion.div
                key="all"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-24 md:space-y-32"
              >
                {groupedAll.length > 1
                  ? groupedAll.map((group, groupIdx) => (
                      <LineSection
                        key={group.line}
                        line={group.line}
                        products={group.items}
                        sectionIndex={groupIdx}
                        linePrefix={linePrefix}
                        productsSuffix={productsSuffix}
                      />
                    ))
                  : (
                    /* Single-line categories: skip line header, show flat grid */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 gap-y-16">
                      {allProducts.map((product, idx) => (
                        <ProductCard key={product.slug} product={product} index={idx} />
                      ))}
                    </div>
                  )}
              </motion.div>
            ) : (
              <motion.div
                key={filter}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45 }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 gap-y-16">
                  {filteredFlat.map((product, idx) => (
                    <ProductCard key={product.slug} product={product} index={idx} />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Optional FAQ section */}
      {faq && (
        <section
          aria-label={faq.title}
          className="relative bg-cream-soft py-24 md:py-32 overflow-hidden"
        >
          <div className="nashi-container">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 1 }}
                className="lg:col-span-5"
              >
                <span className="nashi-eyebrow">{faq.eyebrow ?? defaultFaqEyebrow}</span>
                <h2
                  className="mt-6 font-display text-bruno"
                  style={{
                    fontSize: 'clamp(2.25rem, 4vw, 3.5rem)',
                    lineHeight: 1.02,
                    letterSpacing: '-0.02em',
                  }}
                >
                  <span className="block">{faq.title}</span>
                  {faq.titleItalic && (
                    <span className="block italic font-light text-argan-deep">
                      {faq.titleItalic}
                    </span>
                  )}
                </h2>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 1, delay: 0.15 }}
                className="lg:col-span-7 lg:pt-10"
              >
                <div className="text-bruno-soft text-base md:text-lg leading-relaxed space-y-5">
                  {faq.body}
                </div>

                {(faq.closingNumber || faq.closingNote) && (
                  <div className="mt-10 pt-8 border-t border-bruno/10 flex items-baseline gap-6">
                    {faq.closingNumber && (
                      <span className="font-display italic text-argan-deep text-3xl">
                        {faq.closingNumber}
                      </span>
                    )}
                    {faq.closingNote && (
                      <p className="text-[11px] uppercase tracking-[0.28em] text-bruno-muted">
                        {faq.closingNote}
                      </p>
                    )}
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </section>
      )}

      <Prefooter />
      <Footer />
    </main>
  )
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={[
        'group shrink-0 inline-flex items-baseline gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.18em] transition-all duration-300',
        active
          ? 'bg-bruno text-cream'
          : 'text-bruno hover:text-argan-deep border border-transparent hover:border-argan/30',
      ].join(' ')}
    >
      <span>{label}</span>
      <span
        className={[
          'tabular-nums text-[9px]',
          active ? 'text-argan-soft' : 'text-bruno-muted',
        ].join(' ')}
      >
        {String(count).padStart(2, '0')}
      </span>
    </button>
  )
}

function LineSection({
  line,
  products,
  sectionIndex,
  linePrefix,
  productsSuffix,
}: {
  line: Line
  products: Product[]
  sectionIndex: number
  linePrefix?: string
  productsSuffix?: string
}) {
  return (
    <div>
      <motion.header
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.8 }}
        className="flex items-end justify-between mb-12 md:mb-16 pb-6 border-b border-bruno/10"
      >
        <div>
          <span className="text-[10px] uppercase tracking-[0.32em] text-argan-deep">
            {linePrefix} {String(sectionIndex + 1).padStart(2, '0')}
          </span>
          <h2
            className="mt-3 font-display text-bruno"
            style={{
              fontSize: 'clamp(2rem, 3.6vw, 3.25rem)',
              lineHeight: 1,
              letterSpacing: '-0.01em',
            }}
          >
            {lineLabels[line]}
          </h2>
        </div>
        <span className="hidden md:inline text-[10px] uppercase tracking-[0.32em] text-bruno-muted tabular-nums">
          {String(products.length).padStart(2, '0')} {productsSuffix}
        </span>
      </motion.header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 gap-y-16">
        {products.map((product, idx) => (
          <ProductCard key={product.slug} product={product} index={idx} />
        ))}
      </div>
    </div>
  )
}
